import { useState, useEffect } from 'react';
import { api } from '../../services/api';

interface AnalysisPreviewDialogProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (fileLimit?: number) => void;
  sourceType: string;
  localPath?: string;
}

export function AnalysisPreviewDialog({
  projectId, isOpen, onClose, onConfirm, sourceType: _sourceType, localPath: _localPath
}: AnalysisPreviewDialogProps) {
  const [preview, setPreview] = useState<{
    totalFiles: number;
    breakdown: Record<string, number>;
    estimatedTime: string;
    excludePatterns: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projectId) {
      setLoading(true);
      setError(null);
      api.previewAnalysis(projectId)
        .then(setPreview)
        .catch(e => setError(e.message || 'Failed to scan files'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, projectId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold mb-4">Ready to analyze</h3>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-sm text-gray-500">Scanning files...</p>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm mb-4">{error}</div>
        )}

        {preview && !loading && (
          <>
            <div className="mb-4">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {preview.totalFiles.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">source files found</div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
              {Object.entries(preview.breakdown)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 6)
                .map(([ext, count]) => (
                  <div key={ext} className="flex justify-between px-2 py-1 bg-gray-50 dark:bg-gray-700 rounded">
                    <span className="text-gray-600 dark:text-gray-300">.{ext}</span>
                    <span className="font-medium">{count.toLocaleString()}</span>
                  </div>
                ))}
            </div>

            <div className="text-sm text-gray-500 mb-6">
              Estimated time: {preview.estimatedTime}
            </div>

            {preview.excludePatterns.length > 0 && (
              <div className="text-xs text-gray-400 mb-4">
                Excluded: {preview.excludePatterns.join(', ')}
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm()}
            disabled={loading || !!error}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Analyze All Files
          </button>
        </div>
      </div>
    </div>
  );
}
